import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "item", "empty", "sortable", "status", "loading"]
  static values = { noResults: String, loading: String, loadError: String, loadAllUrl: String }

  connect() {
    this.filter()
  }

  disconnect() {
    window.clearTimeout(this.loadAllTimeout)
    this.loadAllAbortController?.abort()
  }

  filter() {
    const state = this.currentFilterState()
    let visibleItems = 0

    this.scheduleFullListSearch(state)
    this.persistFilterState(state)

    if (this.hasSortableTarget) {
      this.sortableTarget.dataset.sortableDisabledValue = String(state.active)
    }

    this.itemTargets.forEach((item) => {
      const matches = this.itemMatches(item, state)

      item.hidden = !matches
      item.classList.toggle("hidden", !matches)
      if (matches) visibleItems += 1
    })

    this.element.querySelectorAll(".scaffold-grid-limit-notice").forEach((notice) => {
      notice.hidden = state.active
      notice.classList.toggle("hidden", state.active)
    })

    this.updateEmptyState(visibleItems)
  }

  itemMatches(item, state) {
    const searchValue = item.dataset.peopleFilterSearchValue || item.dataset.spacePeopleFilterSearchValue
    const matchesSearch = !state.query || this.normalize(searchValue).includes(state.query)
    if (!matchesSearch) return false

    const planningValues = this.planningValuesFor(item)
    return Object.entries(state.planningFilters).every(([categoryId, selectedValues]) => {
      const currentValue = planningValues[categoryId]?.toString()

      return selectedValues.some((selectedValue) => {
        return selectedValue === "unset" ? !currentValue : currentValue === selectedValue
      })
    })
  }

  planningValuesFor(item) {
    try {
      return JSON.parse(item.dataset.peopleFilterPlanningValues || "{}")
    } catch (_error) {
      return {}
    }
  }

  currentFilterState() {
    const searchInput = this.element.querySelector("[data-people-filter-search]") ||
      (this.hasInputTarget ? this.inputTarget : null)
    const search = searchInput?.value || ""
    const planningFilters = {}

    this.element.querySelectorAll("[data-people-filter-option]:checked").forEach((option) => {
      const categoryId = option.dataset.peopleFilterCategoryId
      const value = option.value
      if (!categoryId || !value) return

      planningFilters[categoryId] ||= []
      if (!planningFilters[categoryId].includes(value)) planningFilters[categoryId].push(value)
    })

    const serializedPlanningFilters = this.serializePlanningFilters(planningFilters)
    const query = this.normalize(search)

    return {
      search,
      query,
      planningFilters,
      serializedPlanningFilters,
      active: Boolean(query || serializedPlanningFilters),
      signature: `${query}\u0000${serializedPlanningFilters}`,
    }
  }

  serializePlanningFilters(planningFilters) {
    return Object.keys(planningFilters)
      .sort((left, right) => Number(left) - Number(right))
      .filter((categoryId) => planningFilters[categoryId].length > 0)
      .map((categoryId) => {
        const values = [...planningFilters[categoryId]].sort((left, right) => {
          if (left === "unset") return 1
          if (right === "unset") return -1
          return Number(left) - Number(right)
        })
        return `${categoryId}:${values.join(",")}`
      })
      .join(";")
  }

  updateEmptyState(visibleItems = this.itemTargets.filter((item) => !item.hidden).length) {
    const showEmpty = visibleItems === 0 && !this.loading && !this.loadError
    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = !showEmpty
      this.emptyTarget.classList.toggle("hidden", !showEmpty)
    }
    if (this.hasStatusTarget) this.statusTarget.textContent = showEmpty ? this.noResultsValue : ""
  }

  scheduleFullListSearch(state) {
    window.clearTimeout(this.loadAllTimeout)
    this.loadAllAbortController?.abort()
    this.setLoadError(false)
    this.setLoading(false)
    if (!state.active || !this.hasLoadAllUrlValue || this.fullListLoaded) return

    this.setLoading(true)
    this.loadAllTimeout = window.setTimeout(() => {
      this.loadFullList(state)
    }, 200)
  }

  async loadFullList(state) {
    const url = new URL(this.loadAllUrlValue, window.location.origin)
    this.applyFilterStateToUrl(url, state)
    const abortController = new AbortController()
    this.loadAllAbortController = abortController
    this.setLoading(true)

    try {
      const response = await fetch(url, {
        headers: { Accept: "text/html" },
        signal: abortController.signal
      })
      if (!response.ok) throw new Error(`People search failed with ${response.status}`)

      const responseDocument = new DOMParser().parseFromString(await response.text(), "text/html")
      const loadedFilter = this.loadedFilterFrom(responseDocument)
      const loadedItems = Array.from(loadedFilter?.querySelectorAll("[data-space-people-filter-target~='item']") || [])
      const existingItemsByKey = new Map(this.itemTargets.map((item, index) => [this.itemKey(item, index), item]))
      if (!loadedFilter || loadedItems.length < existingItemsByKey.size) throw new Error("People search returned an incomplete list")
      if (this.loadAllAbortController !== abortController || this.currentFilterState().signature !== state.signature) return

      if (this.hasSortableTarget) {
        const orderedItems = loadedItems.map((loadedItem, index) => {
          return existingItemsByKey.get(this.itemKey(loadedItem, index)) || loadedItem
        })
        const orderedRows = document.createDocumentFragment()

        this.itemTargets.forEach((item) => item.remove())
        orderedItems.forEach((item) => orderedRows.append(item))
        this.sortableTarget.append(orderedRows)
      }

      this.fullListLoaded = true
      this.element.querySelectorAll(".scaffold-grid-limit-notice").forEach((notice) => notice.remove())
      this.element.removeAttribute("data-space-people-filter-load-all-url-value")
      window.history.replaceState(window.history.state, "", url)
      this.filter()
    } catch (error) {
      if (error.name !== "AbortError" && this.loadAllAbortController === abortController) {
        console.error(error)
        this.setLoadError(true)
      }
    } finally {
      if (this.loadAllAbortController === abortController && this.currentFilterState().signature === state.signature) {
        this.setLoading(false)
        this.updateEmptyState()
      }
    }
  }

  loadedFilterFrom(responseDocument) {
    const rootId = this.element.dataset.spacePeopleFilterRoot
    const filters = Array.from(responseDocument.querySelectorAll("[data-controller~='space-people-filter']"))

    return filters.find((filter) => !rootId || filter.dataset.spacePeopleFilterRoot === rootId)
  }

  itemKey(item, index) {
    return item.dataset.personId || item.dataset.planningContextPerson || item.dataset.id || `item-${index}`
  }

  persistFilterState(state) {
    const url = new URL(window.location.href)
    this.applyFilterStateToUrl(url, state)
    window.history.replaceState(window.history.state, "", url)
  }

  applyFilterStateToUrl(url, state) {
    const search = state.search.trim()
    if (search) {
      url.searchParams.set("people_search", search)
    } else {
      url.searchParams.delete("people_search")
    }

    if (state.serializedPlanningFilters) {
      url.searchParams.set("people_planning_filters", state.serializedPlanningFilters)
    } else {
      url.searchParams.delete("people_planning_filters")
    }
  }

  setLoading(loading) {
    this.loading = loading
    if (!this.hasLoadingTarget) return

    const showStatus = loading || this.loadError
    this.loadingTarget.hidden = !showStatus
    this.loadingTarget.classList.toggle("hidden", !showStatus)
    this.loadingTarget.textContent = loading ? this.loadingValue : (this.loadError ? this.loadErrorValue : "")
  }

  setLoadError(loadError) {
    this.loadError = loadError
    if (!this.hasLoadingTarget) return

    this.loadingTarget.hidden = !loadError
    this.loadingTarget.classList.toggle("hidden", !loadError)
    this.loadingTarget.textContent = loadError ? this.loadErrorValue : ""
  }

  normalize(value) {
    return (value || "")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
  }
}
